/* eslint-disable no-restricted-imports -- F3-1: bu dosya apiClient'ı ÜRETEN tek istisna */
import axios from 'axios';
import { isPrerendering } from './isPrerender';
// F3-1 / HP: API_URL fallback tek doğruluk kaynağı — constants.js
import { API_BASE_URL } from './constants';

// Cross-tab auth event channel.
// The JWT access token is delivered via an HttpOnly + Secure cookie (D1 fix)
// and is therefore shared across tabs automatically by the browser. We never
// store the token in localStorage (XSS hardening). localStorage is used ONLY
// to broadcast non-sensitive login/logout events so idle tabs can react
// (redirect to /login on logout, re-validate session on login).
const AUTH_EVENT_KEY = 'auth_event';

const broadcastAuthEvent = (type) => {
  try {
    // Timestamp guarantees the value changes so the `storage` event fires
    // in other tabs even for repeated events of the same type.
    localStorage.setItem(AUTH_EVENT_KEY, JSON.stringify({ type, ts: Date.now() }));
  } catch (_) {}
};

const getAuthEvent = () => {
  try {
    const raw = localStorage.getItem(AUTH_EVENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  withCredentials: true, // HttpOnly cookie-based auth (D1 security fix)
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // ReactSnap prerendering esnasında canlı backend'e CORS reddedilen
    // localhost origin'inden istek atmasını engelle. Prerender sadece statik
    // iskelet üretmelidir; API çağrıları gerçek tarayıcıda yapılsın.
    if (isPrerendering()) {
      return Promise.reject(new Error('Skipped during prerendering'));
    }

    // Add request timestamp
    config.metadata = { startTime: new Date() };

    // No Authorization header is attached here: authentication relies on the
    // HttpOnly auth_token cookie (sent automatically via withCredentials).
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Track if we're already refreshing to prevent infinite loops
let isRefreshing = false;
let failedQueue = [];

// --- 401 refresh-loop korumaları ----------------------------------------------
// 1) Hard-logout redirect yalnızca auth sayfalarının DIŞINDA çalışır.
//    Auth sayfaları (/login, /auth/google/callback, ...) app mount'ta /auth/me
//    session kontrolü yapar; misafirken 401 → refresh fail → redirect → full
//    reload → tekrar /me 401 → ... sonsuz reload döngüsü buradan doğuyordu.
const AUTH_PAGE_PREFIXES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/auth/google/callback'];
const isOnAuthPage = () => {
  const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
  return AUTH_PAGE_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
};

// 2) Saf misafirde (refresh cookie hiç yok) her 401'de refresh denemek boşa
//    gider ve IP başına refresh-token rate limit bütçesini yakar. Backend
//    "Refresh token is required" (400) dönerse kısa süreliğine (5 dk) refresh
//    denenmez; TTL sayesinde login sonrası kendiliğinden iyileşir.
let refreshMissingUntil = 0;
const REFRESH_MISSING_BACKOFF_MS = 5 * 60 * 1000;
const COOKIE_SETTING_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/google', '/auth/refresh-token'];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Başarılı auth akışları (login/register/oauth/refresh) yeni HttpOnly
    // cookie'ler set eder — "refresh cookie yok" backoff'unu sıfırla.
    if (refreshMissingUntil > 0 &&
        COOKIE_SETTING_ENDPOINTS.some((ep) => (response.config?.url || '').startsWith(ep))) {
      refreshMissingUntil = 0;
    }
    return response;
  },
  async (error) => {
    // Calculate request duration
    const endTime = new Date();
    const duration = error.config?.metadata ? endTime - error.config.metadata.startTime : 0;

    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url} (${duration}ms)`, error.response?.data || error.message);
    }

    const originalRequest = error.config;

    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // K-2 deadlock koruması: refresh isteğinin KENDİ 401'i bu
          // interceptor'a tekrar girerse, isRefreshing=true iken iç istek
          // kuyruğa atılır; onu yalnız processQueue çözebilir ve processQueue
          // da dıştaki await'i beklediğinden dairesel bekleme oluşur —
          // promise asla settle olmaz, isRefreshing sonsuza dek true kalır.
          // Muaf işaretli (refresh) istekleri burada bırak; alttaki ortak
          // normalize-reject ile reddedilsin.
          if (originalRequest?._skipAuthRefresh) {
            break;
          }
          // Try to refresh token via HttpOnly cookie before logging out (D1 fix)
          if (!originalRequest._retry && Date.now() >= refreshMissingUntil) {
            if (isRefreshing) {
              // Queue this request until refresh completes
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              }).then(() => {
                // The refreshed token is delivered via HttpOnly cookie, so we
                // simply replay the original request — no header to set.
                return apiClient(originalRequest);
              }).catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
              // Refresh token cookie is sent automatically (HttpOnly); the
              // server rotates and sets a new auth_token cookie in the response.
              // _skipAuthRefresh: refresh'in kendi 401'i interceptor'ın 401
              // dalına girip kuyruğa düşmesin (K-2 deadlock düzeltmesi).
              // Data bilinçli olarak `undefined`: instance'ın varsayılan
              // Content-Type: application/json başlığıyla `null` geçmek axios
              // 1.x'te literal "null" gövdesi gönderir ve backend'deki strict
              // JSON parser'ı patlatır ("Unexpected token n in JSON").
              await apiClient.post('/auth/refresh-token', undefined, { _skipAuthRefresh: true });
              refreshMissingUntil = 0;

              processQueue(null);
              return apiClient(originalRequest);
            } catch (refreshError) {
              processQueue(refreshError, null);
              // Backend "cookie hiç gelmedi" dedi (saf misafir) — kısa süre
              // boyunca refresh denemesi yapma (rate-limit bütçesi).
              if (refreshError?.status === 400) {
                refreshMissingUntil = Date.now() + REFRESH_MISSING_BACKOFF_MS;
              }
              // Only hard-logout if the original request is not flagged as non-critical.
              // Background calls (e.g. loading favorites after generation) set
              // _skipGlobalLogout so a stale/missing token doesn't boot the user
              // off the page they're currently viewing. Auth sayfalarında da
              // redirect edilmez: bu sayfalarda session kontrolü normalde 401
              // döner ve redirect → full reload → sonsuz döngü üretir.
              if (!originalRequest._skipGlobalLogout && !isOnAuthPage()) {
                // Notify other tabs so they fall back to the home page too.
                broadcastAuthEvent('logout');
                // Kayıt Duvarı modeli: oturum düşen kullanıcı /login'e değil
                // ana sayfaya (misafir) taşınır. '/' misafirken kimlik
                // doğrulamalı istek tetiklemez → reload döngüsü oluşmaz.
                window.location.href = '/';
              }
              return Promise.reject(refreshError);
            } finally {
              isRefreshing = false;
            }
          }
          break;
        
        case 403:
          // Forbidden - show access denied message
          // Special-case EMAIL_NOT_VERIFIED so the UI can prompt the user
          // to check their inbox / resend a verification link instead of
          // showing a generic "access denied" toast.
          if (data?.code === 'EMAIL_NOT_VERIFIED') {
            try {
              window.dispatchEvent(new CustomEvent('auth:email-not-verified', {
                detail: { message: data?.message || data?.error }
              }));
            } catch (_) {}
          } else if (data?.code === 'AUTH_REQUIRED') {
            // Kayıt Duvarı: misafir isteği backend'de AI'ye gönderilmeden
            // kesildi (requireAuthForAI). LimitModalProvider blur + kayıt
            // modalı açar.
            try {
              window.dispatchEvent(new CustomEvent('auth:required', {
                detail: { feature: 'ai' }
              }));
            } catch (_) {}
          } else {
            console.error('Access denied:', data.message);
          }
          break;

        case 404:
          // Not found
          console.error('Resource not found:', data.message);
          break;

        case 429:
          // Too many requests
          console.error('Rate limit exceeded:', data.message);
          // Kayıt Duvarı modeli: login kullanıcının günlük 3 sorgu limiti
          // doldu — global event dispatch et, LimitModalProvider
          // DailyLimitUpgradeModal'ı açar.
          try {
            if (data?.code === 'DAILY_LIMIT_EXCEEDED') {
              window.dispatchEvent(new CustomEvent('limit:daily-exceeded', {
                detail: data
              }));
            }
          } catch (_) {}
          break;
        
        case 500:
          // Server error
          console.error('Server error:', data.message);
          break;
        
        default:
          console.error('API error:', data.message || error.message);
      }

      // Return standardized error
      // Y-3 (cozum2.md): `response` alanını da taşı — çağıran yerler
      // `error.response?.data` okuyabilsin (axios konvensiyonu). Böylece
      // apiClient'ı doğrudan kullanan bileşenlerin hata mesajları kaybolmaz.
      return Promise.reject({
        status,
        message: data.message || 'An error occurred',
        data: data,
        response: error.response,
        original: error
      });
    } else if (error.request) {
      // Network error
      console.error('🔍 [API] Network error details:');
      console.error('  - Error message:', error.message);
      console.error('  - Request readyState:', error.request.readyState);
      console.error('  - Request status:', error.request.status);
      console.error('  - Request responseURL:', error.request.responseURL);
      console.error('  - Request responseText:', error.request.responseText);
      console.error('  - Request responseType:', error.request.responseType);
      
      return Promise.reject({
        status: 0,
        message: 'Network error. Please check your connection.',
        original: error
      });
    } else {
      // Other error
      console.error('Request error:', error.message);
      return Promise.reject({
        status: 0,
        message: error.message || 'An unexpected error occurred',
        original: error
      });
    }
  }
);

// API methods
export const api = {
  // GET request
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config);
      return { success: true, data: response.data, response };
    } catch (error) {
      return { success: false, error, data: null };
    }
  },

  // POST request
  // Not: axios 1.x'te `data=null` literal "null" gövdesi gönderir; backend'in
  // strict JSON parser'ı bunu reddeder ("Unexpected token 'n'..."). Gövdesiz
  // isteklerde undefined geçilir → gövde hiç gönderilmez.
  post: async (url, data = null, config = {}) => {
    try {
      const response = await apiClient.post(url, data ?? undefined, config);
      return { success: true, data: response.data, response };
    } catch (error) {
      return { success: false, error, data: null };
    }
  },

  // PUT request
  put: async (url, data = null, config = {}) => {
    try {
      const response = await apiClient.put(url, data ?? undefined, config);
      return { success: true, data: response.data, response };
    } catch (error) {
      return { success: false, error, data: null };
    }
  },

  // DELETE request
  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return { success: true, data: response.data, response };
    } catch (error) {
      return { success: false, error, data: null };
    }
  },

  // PATCH request
  patch: async (url, data = null, config = {}) => {
    try {
      const response = await apiClient.patch(url, data ?? undefined, config);
      return { success: true, data: response.data, response };
    } catch (error) {
      return { success: false, error, data: null };
    }
  },

  // Upload file
  upload: async (url, file, additionalData = {}, onUploadProgress = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add additional data to form
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onUploadProgress || ((progressEvent) => {
          // percentCompleted calculation removed - not used
        }),
      };

      const response = await apiClient.post(url, formData, config);
      return { success: true, data: response.data, response };
    } catch (error) {
      return { success: false, error, data: null };
    }
  },

  // Download file
  download: async (url, filename = null, config = {}) => {
    try {
      const response = await apiClient.get(url, {
        ...config,
        responseType: 'blob'
      });

      // Create blob URL
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true, data: response.data, response };
    } catch (error) {
      return { success: false, error, data: null };
    }
  }
};

// Specific API endpoints
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/me'),
  get: (url) => api.get(url), // Generic GET method
  updateProfile: (data) => api.put('/auth/me', data),
  changePassword: (currentPassword, newPassword) => 
    api.put('/auth/password', { currentPassword, newPassword }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  deleteAccount: () => api.delete('/auth/delete-account'),
};

// Feature A — Sınırsız erişim (abonelik) yönetimi
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription/status'),
  cancel: () => api.post('/subscription/cancel', {}),
  reactivate: () => api.post('/subscription/reactivate', {}),
};

// Feature B + Faz 1.2 — Faturalar
export const invoiceAPI = {
  list: () => api.get('/invoices'),
  downloadUrl: (id) => `${API_BASE_URL}/invoices/${id}/download`,
  download: (id, filename) => api.download(`/invoices/${id}/download`, filename),
};

// Faz 1.3 — Pazarlama izni
export const marketingAPI = {
  updateConsent: (consent) => api.put('/users/me/marketing-consent', { consent }),
};

// Ucretsiz PDF kampanyasi (Lead Magnet)
export const leadMagnetAPI = {
  // Aktif kampanya PDF'leri (+ girisli kullanici icin claim durumu)
  list: () => api.get('/lead-magnets'),
  // Ucretsiz PDF talep et (marketing_consent zorunlu; 403 -> MARKETING_CONSENT_REQUIRED)
  // ref: sosyal medya paylasim linki kodu (opsiyonel, attribution icin)
  claim: (magnetId, ref) => api.post(`/lead-magnets/${magnetId}/claim`, ref ? { ref } : {}),
  downloadUrl: (token) => `${API_BASE_URL}/lead-magnets/download/${token}`,
  // Sosyal medya paylasim linki click takibi (public, sessiz)
  trackLinkClick: (code) => api.post(`/lead-magnets/links/${code}/click`, {}),
  // Sosyal medya paylasim linkinden direkt (kayitsiz) PDF indirme URL'si
  directDownloadUrl: (code) => `${API_BASE_URL}/lead-magnets/dl/${code}`,
  // Admin
  adminList: () => api.get('/admin/lead-magnets'),
  adminCreate: (data) => api.post('/admin/lead-magnets', data),
  adminUpdate: (id, data) => api.put(`/admin/lead-magnets/${id}`, data),
  adminDelete: (id) => api.delete(`/admin/lead-magnets/${id}`),
  // Admin: paylasim linkleri (kaynak basina tek, idempotent)
  adminListLinks: (id) => api.get(`/admin/lead-magnets/${id}/links`),
  adminCreateLink: (id, source) => api.post(`/admin/lead-magnets/${id}/links`, { source }),
  adminUpdateLink: (linkId, data) => api.put(`/admin/lead-magnets/links/${linkId}`, data),
  adminDeleteLink: (linkId) => api.delete(`/admin/lead-magnets/links/${linkId}`),
  adminUploadPdf: (id, formData, onProgress) =>
    api.post(`/admin/lead-magnets/${id}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onProgress
    }),
  // Kapak/onerme gorseli yukle (JPEG/PNG/WebP, max 5MB)
  adminUploadImage: (id, formData) =>
    api.post(`/admin/lead-magnets/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  adminDeleteImage: (id) => api.delete(`/admin/lead-magnets/${id}/image`),
};

// Günlük AI kotası (Kayıt Duvarı). Kredi API'si kaldırıldı.
export const quotaAPI = {
  getDailyQuota: () => api.get('/quota/daily-quota'),
};

export const suggestionAPI = {
  generate: (preferences) => api.post('/suggestions/generate', preferences),
  getHistory: (limit = 50, offset = 0) => api.get(`/suggestions/history?limit=${limit}&offset=${offset}`),
  getSuggestion: (id) => api.get(`/suggestions/${id}`),
  updateFavorite: (id, isFavorite) => api.patch(`/suggestions/${id}/favorite`, { isFavorite }),
  deleteSuggestion: (id) => api.delete(`/suggestions/${id}`),
};

export const paymentAPI = {
  
  createPayPalPayment: (amount, currency, packageId) => 
    api.post('/payments/paypal', { amount, currency, package_id: packageId }),
  getPaymentStatus: (paymentId) => api.get(`/payments/status/${paymentId}`),
  getPaymentHistory: (limit = 50, offset = 0) => api.get(`/payments/history?limit=${limit}&offset=${offset}`),

  // Unlimited (sınırsız erişim) paketleri — donusumSonuc.md (15 May 2026)
  getUnlimitedPackages: (lang = 'tr') => api.get(`/payments/paypal/unlimited/packages?lang=${lang}`),
  createUnlimitedOrder: (packageId, returnUrl, cancelUrl) =>
    api.post('/payments/paypal/unlimited/create', { packageId, returnUrl, cancelUrl }),
  executeUnlimitedOrder: (token, paymentId, PayerID) =>
    api.post('/payments/paypal/unlimited/execute', { token, paymentId, PayerID }),
};

export const languageAPI = {
  getSupportedLanguages: () => api.get('/languages'),
  getTranslations: (languageCode) => api.get(`/languages/${languageCode}/translations`),
  getUserLanguage: () => api.get('/languages/user/current'),
  updateUserLanguage: (languageCode) => api.put('/languages/user/preference', { language: languageCode }),
  validateLanguage: (languageCode) => api.post('/languages/validate', { language: languageCode }),
  getLanguageStats: () => api.get('/languages/stats'),
};



export const consultingAPI = {
  
  // Kullanıcının danışmanlık taleplerini getir
  getMyRequests: (page = 1, limit = 10) => 
    api.get(`/consulting/my-requests?page=${page}&limit=${limit}`),
  
  // Müsait randevu saatlerini getir
  getAvailableSlots: (startDate = null, endDate = null, date = null) => {
    let url = '/consulting/available-slots';
    const params = new URLSearchParams();
    
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (date) params.append('date', date);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    return api.get(url);
  },
  
  // Randevu iptal et
  cancelAppointment: (requestId) => 
    api.put(`/consulting/cancel/${requestId}`),
  
  // Admin: Yeni slot'lar oluştur
  createSlots: (days = 30) => 
    api.post('/consulting/admin/create-slots', { days }),

  // User: Kullanıcının randevularını getir
  getUserAppointments: (page = 1, limit = 10, status = 'all') => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      status: status
    });
    
    return api.get(`/users/appointments?${params}`);
  },

  // PayPal direct payment — Step 1: create order
  createOrder: (formData) =>
    api.post('/consulting/create-order', formData),

  // PayPal direct payment — Step 2: capture + create consulting request
  executePayment: (orderId) =>
    api.post('/consulting/execute-payment', { orderId }),

  // Admin ücretsiz rezervasyon (yalnızca local/dev; backend env guard'ı ile).
  // Ödeme/slot ücreti alınmadan canlı chat randevusu oluşturur.
  adminFreeBooking: (formData) =>
    api.post('/consulting/admin/free-booking', formData),

  // ── Canlı chat odası (consulting.md — Faz 4/5) ──────────────────────────
  // Kullanıcı tarafı magic-link token ile; auth gerekmez.
  getRoom: (token, after = 0, lang = null, config = {}) => {
    const langParam = lang ? `&lang=${encodeURIComponent(lang)}` : '';
    return api.get(`/consulting/room/${token}?after=${after}${langParam}`, config);
  },

  sendRoomMessage: (token, body) =>
    api.post(`/consulting/room/${token}/messages`, { body }),

  sendRoomFeedback: (token, rating, comment = null) =>
    api.post(`/consulting/room/${token}/feedback`, { rating, comment }),

  // Uzatma ödemesi (Faz 6)
  createExtensionOrder: (token) =>
    api.post(`/consulting/room/${token}/extend-order`),

  executeExtensionPayment: (token, orderId) =>
    api.post(`/consulting/room/${token}/extend-payment`, { orderId }),

  // Admin oda yönetimi (Faz 7)
  adminGetSessions: (status = 'all', config = {}) =>
    api.get(`/consulting/admin/sessions?status=${status}`, config),

  adminStartSession: (id) =>
    api.post(`/consulting/admin/sessions/${id}/start`),

  adminGetRoom: (id, after = 0, config = {}) =>
    api.get(`/consulting/admin/sessions/${id}?after=${after}`, config),

  adminSendMessage: (id, body) =>
    api.post(`/consulting/admin/sessions/${id}/messages`, { body }),

  // DEV-ONLY: toplantı zamanını kaydır (yalnızca production dışı ortam).
  adminDevShift: (id, minutes) =>
    api.post(`/consulting/admin/sessions/${id}/dev-shift`, { minutes }),

  adminGetFeedback: () =>
    api.get('/consulting/admin/feedback')
};

export const nameSearchAPI = {
  // İsim arama
  searchName: (query, language = 'tr', islamicAnalysis = false) => {
    const params = new URLSearchParams({
      query: query,
      language: language
    });
    
    if (islamicAnalysis) {
      params.append('islamic_analysis', 'true');
    }
    
    return api.get(`/names/search?${params}`);
  },
  
  // İsim detayları
  getNameDetails: (name, language = 'tr') => 
    api.get(`/names/${encodeURIComponent(name)}/details?language=${language}`),
  
  // Otomatik tamamlama önerileri
  getSearchSuggestions: (partial, limit = 5) => 
    api.get(`/names/suggestions?partial=${encodeURIComponent(partial)}&limit=${limit}`),
  
  // Popüler isimler
  getPopularNames: (limit = 10, gender = 'all') => 
    api.get(`/names/popular?limit=${limit}&gender=${gender}`),
  
  // Kullanıcı favorileri
  getUserFavorites: (userId) => {
    if (!userId) throw new Error('User ID is required');
    // _skipGlobalLogout: a 401 here (e.g. stale token after async generation)
    // should fail silently rather than kicking the user off the page.
    return api.get(`/users/${userId}/favorites`, { _skipGlobalLogout: true });
  },
  
  // Favori toggle (ekleme)
  toggleFavorite: (userId, name, nameData = {}) => {
    if (!userId) throw new Error('User ID is required');
    return api.post(`/users/${userId}/favorites`, {
      name,
      meaning: nameData.meaning,
      origin: nameData.origin,
      gender: nameData.gender
    });
  },
  
  // Favori silme
  removeFavorite: (userId, name) => {
    if (!userId) throw new Error('User ID is required');
    return api.delete(`/users/${userId}/favorites`, {
      data: { name }
    });
  },
  
  // AI ile isim arama (AI zinciri GLM→DS→Gemini: 25+45+35s, 120s timeout)
  searchWithAI: (name, language = 'tr') =>
    api.post('/names/ai-search', { name, language }, { timeout: 120000 }),

  // İslami uygunluk analizi (İkinci Aşama) - AI zinciri uzun sürebilir, 120s timeout
  islamicAnalysis: (name, language = 'tr') =>
    api.post('/names/islamic-analysis', { name, language }, { timeout: 120000 }),

  // İsim Kişiliği analizi (Name Personality) - AI zinciri uzun sürebilir, 120s timeout
  getPersonality: (name, language = 'tr') =>
    api.get(`/names/${encodeURIComponent(name)}/personality?language=${language}`, { timeout: 120000 }),

  // İsim Uyum Skoru + Benzer İsimler (Name Harmony)
  // AI zinciri GLM→DS→Gemini: 25+45+35s + DB. 120s timeout — nginx ile uyumlu.
  getHarmony: (name, siblingName, surname, language = 'tr') =>
    api.post('/names/harmony', { name, siblingName, surname, language }, { timeout: 120000 }),

  // Çoklu isim karşılaştırma (tek AI çağrısı; zincir bütçesi 25+45+35s, 120s timeout)
  compareNames: (names, language = 'tr') =>
    api.post('/names/compare', { names, language }, { timeout: 120000 }),

  // Anonim PDF email gönderimi
  sendPdf: (name, email, language = 'tr') =>
    api.post('/names/send-pdf', { name, email, language }, { timeout: 30000 }),

  // Anonim isim kaydetme
  saveAnonymous: (name, email) =>
    api.post(`/names/${encodeURIComponent(name)}/save-anonymous`, { email }),
};

export const tinderAPI = {
  // Yeni oturum oluştur (AI isim üretimi uzun sürebilir, 120s timeout)
  createSession: (namePreference, motherEmail, fatherEmail, language = 'de') =>
    api.post('/tinder-session/create', { namePreference, motherEmail, fatherEmail, language }, { timeout: 120000 }),
  
  // Oturumdaki isimleri getir
  getSessionNames: (sessionId) =>
    api.get(`/tinder-session/${sessionId}/names`),
  
  // Swipe kaydı gönder (geriye uyumluluk - tekil swipe)
  recordSwipe: (sessionId, nameIndex, liked, role) =>
    api.post(`/tinder-session/${sessionId}/swipe`, { nameIndex, liked, role }),
  
  // Değerlendirmeyi tamamla (toplu swipe desteği ile)
  completeSession: (sessionId, role, swipes = null) =>
    api.post(`/tinder-session/${sessionId}/complete`, { role, ...(swipes ? { swipes } : {}) }),
  
  // Davet tokeni ile oturuma eriş
  accessByInvite: (token) =>
    api.get(`/tinder-session/invite/${token}`),
  
  // Eşleşme sonuçlarını getir
  getResults: (sessionId) =>
    api.get(`/tinder-session/${sessionId}/results`)
};

export const ttsAPI = {
  // TTS ses üretimi (cache kontrolü dahil, origin ile doğru telaffuz).
  // useOrigin=true: origin'in kendi diliyle telaffuz et (örn. "Arabic" → ar-SA).
  generate: (name, language = 'tr', origin = null, useOrigin = false) =>
    api.post('/tts/generate', {
      name,
      language,
      ...(origin && { origin }),
      ...(useOrigin && { useOrigin: true }),
    }),
  
  // Cache'den ses getir (kredi gerektirmez)
  getCached: (name, lang = 'tr') =>
    api.get(`/tts/${encodeURIComponent(name)}/${lang}`),
  
  // Cache kontrolü (lightweight)
  checkCache: (name, lang = 'tr') =>
    api.get(`/tts/check/${encodeURIComponent(name)}/${lang}`)
};

// Admin E-Kitap API
const uploadFile = async (url, formData, onUploadProgress) => {
  try {
    const response = await apiClient.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 900000 // 15 minutes for large ebook files (up to 250MB)
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error, data: null };
  }
};

export const ebookAdminAPI = {
  // Ebook CRUD
  createEbook: (data) => api.post('/admin/ebooks', data),
  getAllEbooks: (params) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') queryParams.append(key, val);
      });
    }
    const qs = queryParams.toString();
    return api.get(`/admin/ebooks${qs ? '?' + qs : ''}`);
  },
  getEbook: (id) => api.get(`/admin/ebooks/${id}`),
  updateEbook: (id, data) => api.put(`/admin/ebooks/${id}`, data),
  deleteEbook: (id) => api.delete(`/admin/ebooks/${id}`),

  // File Upload
  uploadPdf: (id, formData, onUploadProgress) =>
    uploadFile(`/admin/ebooks/${id}/upload-pdf`, formData, onUploadProgress),
  uploadSample: (id, formData, onUploadProgress) =>
    uploadFile(`/admin/ebooks/${id}/upload-sample`, formData, onUploadProgress),
  uploadCover: (id, formData, onUploadProgress) =>
    uploadFile(`/admin/ebooks/${id}/upload-cover`, formData, onUploadProgress),

  // Publishing
  publishEbook: (id) => api.put(`/admin/ebooks/${id}/publish`, {}),
  unpublishEbook: (id) => api.put(`/admin/ebooks/${id}/unpublish`, {}),

  // Stats & Purchases
  getStats: () => api.get('/admin/ebooks/stats'),
  getEbookPurchases: (id, params) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') queryParams.append(key, val);
      });
    }
    const qs = queryParams.toString();
    return api.get(`/admin/ebooks/${id}/purchases${qs ? '?' + qs : ''}`);
  },
};

// Public E-Kitap API
export const ebookAPI = {
  // Public - Ayarlar (min EUR fiyati vb.; tek kaynak backend config)
  getConfig: () => api.get('/ebooks/config'),

  // Public - Listeleme
  getEbooks: (params) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') queryParams.append(key, val);
      });
    }
    const qs = queryParams.toString();
    return api.get(`/ebooks${qs ? '?' + qs : ''}`);
  },
  getEbook: (idOrSlug) => api.get(`/ebooks/${idOrSlug}`),
  getSampleUrl: (id) => {
    if (process.env.NODE_ENV === 'development') {
      return `/api/ebooks/${id}/sample`;
    }
    return `${API_BASE_URL}/ebooks/${id}/sample`;
  },

  // PayPal ile satin alma
  createPayPalOrder: (ebookId, recaptchaToken) =>
    api.post('/ebooks/purchase/paypal/create', { ebookId, recaptcha_token: recaptchaToken }),
  executePayPalOrder: (ebookId, paypalOrderId) =>
    api.post('/ebooks/purchase/paypal/execute', { ebookId, paypalOrderId }),

  // Kullanici satin alimlari
  getMyPurchases: () => api.get('/ebooks/my-purchases'),
  getDownloadUrl: (token) => `${API_BASE_URL}/ebooks/download/${token}`,
};

// Public (auth gerektirmeyen) endpoint'ler — Faz 0 / Faz 4
export const publicAPI = {
  getStats: () => api.get('/public/stats'),
  getSettings: () => api.get('/public/settings'),
};

// Admin Site Settings — Faz 0 / Faz 4
export const adminSettingsAPI = {
  getSettings: () => api.get('/admin/settings'),
  updateSetting: (key, value) => api.put('/admin/settings', { key, value }),
};

// Admin Name Management — Faz 3.4
export const nameAdminAPI = {
  list: ({ page = 1, limit = 25, search = '', gender = '' } = {}) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    if (search) params.set('search', search);
    if (gender) params.set('gender', gender);
    return api.get(`/admin/names?${params.toString()}`);
  },
  create: (data) => api.post('/admin/names', data),
  update: (id, data) => api.put(`/admin/names/${id}`, data),
  remove: (id) => api.delete(`/admin/names/${id}`),
};

export { broadcastAuthEvent, getAuthEvent, AUTH_EVENT_KEY }; // cross-tab auth sync (non-sensitive events only)

export default apiClient; 